import { IClient } from '@api/models';
import { Avatar, Checkbox, CircularProgress, Input } from '@hyperflake/react-ui-library';
import { SearchIcon } from '@icon/index';
import useClientListData from 'modules/clients/hooks/useClientListData';
import { useState } from 'react';

interface ClientListProps {
    clients: IClient[];
    isLoading: boolean;
    selectedIds: string[];
    setSelectedIds: React.Dispatch<React.SetStateAction<string[]>>;
    selectAllChecked: boolean;
    setSelectAllChecked: React.Dispatch<React.SetStateAction<boolean>>;
    searchValue: string;
    setSearchValue: React.Dispatch<React.SetStateAction<string>>;
}

const ClientList = ({
    clients,
    isLoading,
    selectedIds,
    setSelectedIds,
    selectAllChecked,
    setSelectAllChecked,
    searchValue,
    setSearchValue,
}: ClientListProps) => {
    const filteredClients = clients?.filter((client) => client.name.toLowerCase().includes(searchValue.toLowerCase()));

    const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
        const filteredIds = filteredClients.map((client) => client._id);
        if (e.target.checked) {
            // Merge filtered ids into existing selected ids (preserve selections outside search)
            setSelectedIds((prev) => [...new Set([...prev, ...filteredIds])]);
            setSelectAllChecked(true);
        } else {
            // Only remove ids that are in the current filtered results
            setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
            setSelectAllChecked(false);
        }
    };

    const handleCheckboxChange = (checked: boolean, rowId: string) => {
        if (checked) {
            setSelectedIds((prev) => [...prev, rowId]);
        } else {
            setSelectedIds((prev) => prev.filter((id) => id !== rowId));
            setSelectAllChecked(false);
        }
    };

    // Reflect selectAll state based on filtered results
    const isAllFilteredSelected =
        filteredClients?.length > 0 && filteredClients.every((client) => selectedIds.includes(client._id));

    return (
        <>
            {/* Select All + Search */}
            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2">
                        <SearchIcon className="text-grayscale-400" size={16} />
                    </div>
                    <Input
                        className="pl-9"
                        placeholder="Search for clients..."
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                    />
                </div>
            </div>

            <div className="flex items-center justify-between gap-3 mb-4">
                <label className="flex items-center gap-2 text-sm font-medium text-grayscale-600 cursor-pointer">
                    <Checkbox color="primary" checked={isAllFilteredSelected} onChange={handleSelectAll} />
                    Select All
                </label>

                <div className="text-xs font-medium text-grayscale-500">
                    {selectedIds.length} of {clients?.length} clients selected
                </div>
            </div>

            {/* Client List */}
            <div className="space-y-1 max-h-[550px] overflow-y-scroll">
                {isLoading && (
                    <div className="flex items-center justify-center py-10">
                        <CircularProgress />
                    </div>
                )}

                {!isLoading &&
                    filteredClients.map((row) => (
                        <label
                            key={row._id}
                            className={`flex items-center gap-3 cursor-pointer px-4 py-3 rounded-lg border border-grayscale-100 hover:bg-grayscale-50 transition-colors ${
                                selectedIds.includes(row._id) ? 'border-primary bg-primary/5' : 'hover:bg-grayscale-100'
                            }`}
                        >
                            <Avatar src={row?.logoUrl} alt={row?.name} bgColor={row?.preferences?.primaryColor} />

                            <div className="overflow-hidden">
                                <p className="font-medium text-sm truncate">{row.name}</p>
                                <p className="font-medium text-xs text-grayscale-400 truncate">{row._id}</p>
                            </div>
                            <div className="ml-auto">
                                <Checkbox
                                    color="primary"
                                    checked={selectedIds.includes(row._id)}
                                    onChange={(e) => handleCheckboxChange(e.target.checked, row._id)}
                                />
                            </div>
                        </label>
                    ))}

                {!isLoading && filteredClients.length === 0 && (
                    <div className="font-medium text-sm text-grayscale-500">No clients found</div>
                )}
            </div>
        </>
    );
};

export default ClientList;
